import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ADMIN_ROLES, EMPLOYER_ROLES, ROLE_LABELS } from "@/lib/constants";
import AdminDashboard from "@/components/dashboards/AdminDashboard";
import EmployerDashboard from "@/components/dashboards/EmployerDashboard";
import CandidateDashboard from "@/components/dashboards/CandidateDashboard";
import RecruiterDashboard from "@/components/dashboards/RecruiterDashboard";
import NotificationsBell from "@/components/NotificationsBell";
import { Logo } from "@/components/Logo";
import { LogOut } from "lucide-react";

const RECRUITER_ROLES = ["recruteur_lcs", "assistant_recruteur"];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  if (!user) return null;

  let content;
  if (ADMIN_ROLES.includes(user.role)) {
    content = <AdminDashboard />;
  } else if (EMPLOYER_ROLES.includes(user.role)) {
    content = <EmployerDashboard />;
  } else if (RECRUITER_ROLES.includes(user.role)) {
    content = <RecruiterDashboard />;
  } else if (user.role === "candidat") {
    content = <CandidateDashboard />;
  } else {
    content = (
      <div data-testid="dashboard-pending-role" className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-500">
        Votre espace {ROLE_LABELS[user.role] || user.role} sera disponible prochainement.
      </div>
    );
  }

  const subNav = [];
  if (ADMIN_ROLES.includes(user.role) || RECRUITER_ROLES.includes(user.role)) {
    subNav.push({ to: "/talent", label: "Talent Pool" }, { to: "/pipeline", label: "Pipeline ATS" }, { to: "/prospection", label: "Prospection" });
  }
  if (ADMIN_ROLES.includes(user.role)) {
    subNav.push({ to: "/admin/pilotage", label: "Pilotage" }, { to: "/revenus", label: "Revenus" });
  } else if (EMPLOYER_ROLES.includes(user.role)) {
    subNav.push({ to: "/pipeline", label: "Pipeline ATS" }, { to: "/talent", label: "Talent Pool" }, { to: "/billing", label: "Facturation" });
  } else if (user.role === "candidat") {
    subNav.push({ to: "/jobs", label: "Trouver un emploi" }, { to: "/portfolio", label: "Mon portfolio" });
  }

  return (
    <div data-testid="dashboard" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="dashboard-logo-link" aria-label="NEXA — Accueil">
            <Logo />
          </Link>
          <div className="flex items-center gap-4">
            <NotificationsBell />
            <div className="text-right hidden sm:block">
              <p data-testid="dashboard-user-name" className="text-sm font-medium text-slate-900">{user.name}</p>
              {user.nexa_id && (
                <p data-testid="dashboard-user-handle" className="text-xs text-brand-turquoise font-medium">@{user.nexa_id}</p>
              )}
              <p data-testid="dashboard-user-role" className="text-xs text-slate-500">{ROLE_LABELS[user.role] || user.role}</p>
            </div>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-slate-600 hover:text-brand-navy border border-slate-300 rounded-lg px-4 py-2 hover:bg-slate-100 transition-colors duration-200"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <h1 data-testid="dashboard-welcome" className="text-2xl font-bold text-slate-900 mb-4">
          Bonjour, {user.name}
        </h1>
        {subNav.length > 0 && (
          <nav data-testid="dashboard-subnav" className="flex gap-2 mb-8" aria-label="Navigation espace">
            {subNav.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`subnav-${item.to.replaceAll("/", "")}`}
                className="text-sm font-medium text-brand-navy bg-white border border-slate-200 rounded-lg px-4 py-2 hover:border-brand-turquoise hover:text-brand-turquoise transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        )}
        {content}
      </main>
    </div>
  );
}
